import { DEFAULT_LOCALE } from '../constants';
import type { BasePrice, BaseSavedPrice } from '../graphql/v2';

// [ECOM-41] @TODO: Our app still uses 'jp' on the url, so we need to reparse it
// until we finally migrate all codebase to standard 'ja'
export type Language = 'en' | 'de' | 'fr' | 'zh' | 'es' | 'ja';

export type UrlLanguage = Omit<Language, 'ja'> | 'jp';

export const getLocaleFromPathname = (pathname: string): string =>
  // changing this could be spicy, type cast to avoid breaking prod for now
  // @TODO what to do with empty? change return type to string | undefined? default to "en"? error?
  (pathname.replace(/^\//, '').split('/') as [string, ...string[]])[0];

export const getUrlLocale = (locale: string): UrlLanguage =>
  locale === 'ja' ? 'jp' : locale;

export const getISOLocale = (locale: string): Language => {
  const ISOLocale = locale === 'jp' ? 'ja' : (locale as Language);
  return supportedLanguagesFromUrl.includes(locale)
    ? ISOLocale
    : DEFAULT_LOCALE;
};

export const supportedLanguagesFromUrl: UrlLanguage[] = [
  'en',
  'de',
  'es',
  'fr',
  'jp', // this is not a standard locale... old NI problem
  'zh',
];

export const supportedLanguages: Language[] = [
  // ISO-standard locales
  'en',
  'de',
  'es',
  'fr',
  'ja',
  'zh',
];

export const languageNames: Record<Language, string> = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  ja: '日本語',
  zh: '简体中文',
};

export function isValidUrlLanguage(lang: unknown): lang is UrlLanguage {
  return typeof lang === 'string' && supportedLanguagesFromUrl.includes(lang);
}

export function replaceLanguageInUrl(url: string, newLang: UrlLanguage) {
  const parsedUrl = new URL(url);

  // pathname into segments: "/en/pricing/" -> ["en", "pricing"]
  const [lang, ...segments] = parsedUrl.pathname.split('/').filter(Boolean);

  if (isValidUrlLanguage(lang)) {
    parsedUrl.pathname = `/${[newLang, ...segments].join('/')}`;
  }

  return parsedUrl.toString();
}

export type Translation = {
  language?: Language;
  country?: string;
  messages?: { [key: string]: string };
};

const CODE_CURRENCIES = ['AUD', 'CAD'];

export const tPrice = ({
  price,
  translation,
}: {
  price: BasePrice | BaseSavedPrice;
  translation: Translation;
}) => {
  const locale = `${translation.language}-${translation.country}`;

  const formattedPrice = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: price.currency,
    currencyDisplay: CODE_CURRENCIES.includes(price.currency)
      ? 'code'
      : undefined,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price.value);

  if (formattedPrice.includes('AUD')) {
    return formattedPrice.replace('AUD', 'AU $');
  }

  if (formattedPrice.includes('CAD')) {
    return formattedPrice.replace('CAD', 'CA $');
  }

  return formattedPrice;
};

export const t = (
  translation: Translation,
  id: string,
  config?: {
    fallback?: string;
    values?: Record<string, string | number>;
  },
): string => {
  const template = translation.messages?.[id] || config?.fallback;

  if (!template) {
    throw new Error(
      `Invalid translation key or fallback provided for id: ${id}`,
    );
  }

  return config?.values
    ? template.replace(
        /{(\w+)}/g,
        (match: string, key: string) => `${config?.values?.[key]}` || match,
      )
    : template;
};

export const getDefaultUrlLocale = (language: UrlLanguage = DEFAULT_LOCALE) => {
  switch (language) {
    case 'en':
      return 'en-US';
    case 'de':
      return 'de-DE';
    case 'es':
      return 'es-ES';
    case 'fr':
      return 'fr-FR';
    case 'jp':
      return 'ja-JP';
    case 'zh':
      return 'zh-CN';
    default:
      return 'en-US';
  }
};
